import { computed, defineComponent, PropType } from 'vue';
import { IUnaAlert, IUnaAlertStates } from "@/app/shared/components/kl-maprequest-una-table/components/kl-una-alert/kl-una-alert";
import { IRespondersAccordion, MapRequestExtendedResponderData } from "@/app/shared/components/kl-maprequest-una-table/kl-maprequest-una-table";
import { IUnaMessage } from "@/app/shared/components/kl-maprequest-una-table/components/kl-una-message/kl-una-message";
import {MapRequestDetailStatus, MapRequestKlimData} from '@/api/klip-api.proxy';
import { isResponderError, isResponderSuccess, parseResponderTitle } from '@/app/shared/helpers/responder-helper';
import orderBy from "lodash-es/orderBy";
import {escape} from 'html-escaper';

export default defineComponent({
    name: 'KlMaprequestResponders',
    props: {
        responders: {
            type: Object as PropType<MapRequestExtendedResponderData[]>,
            required: true,
        },
        klimData: {
            type: Object as PropType<MapRequestKlimData>,
            required: false,
        },
        mapRequestStatus: {
            type: Number as PropType<MapRequestDetailStatus>,
            required: true,
        },
        respondersStatusMessage: {
            type: String,
            required: false,
        }
    },
    setup(props) {
        const helpLink: string = import.meta.env.VITE_LINKS_MAPREQUESTSTATUSHELP;

        const getAlertStatusFromMapRequestStatus = (status: MapRequestDetailStatus): IUnaAlertStates | null => {
            switch (status) {
                case MapRequestDetailStatus.Created:
                case MapRequestDetailStatus.InProgress:
                    return IUnaAlertStates.error;
                case MapRequestDetailStatus.ActiveIncomplete:
                case MapRequestDetailStatus.Incomplete:
                    return IUnaAlertStates.warning;
                case MapRequestDetailStatus.Active:
                    return IUnaAlertStates.success;
                default:
                    return null;
            }
        };

        const escapeText = (textToEscape: string) => {
            if(!textToEscape) return;
            return escape(textToEscape);
        }

        const unaUnknown = computed(() => {
            const responders = props.responders || [];
            const klimResponders = props.klimData?.mapRequestKlimResponders || [];
            return props.mapRequestStatus === MapRequestDetailStatus.Created && !klimResponders.length && !responders.length;
        });

        const unaAlert = computed((): IUnaAlert => {
            const status = getAlertStatusFromMapRequestStatus(props.mapRequestStatus);

            if (status && props.respondersStatusMessage) {
                return {
                    message: props.respondersStatusMessage,
                    status,
                };
            }
            return undefined;
        });

        const unaResponders = computed((): IRespondersAccordion[] => {
            const klipResponders: MapRequestExtendedResponderData[] = props.responders || [];
            const klimResponders: MapRequestExtendedResponderData[] = ((props.klimData || props.klimData)?.mapRequestKlimResponders || [])
                .map((responder: any) => ({ ...responder, klim: true }));

            const allResponders = [...klipResponders, ...klimResponders];
            const sortedResponders = orderBy(allResponders, ['klim', 'name', 'zoneName'], ['desc', 'asc', 'asc']);

            return sortedResponders.map((responder: MapRequestExtendedResponderData): IRespondersAccordion => ({
                title: parseResponderTitle(responder),
                subtitle: escapeText(responder.extraInformation),
                statusMessage: escapeText(responder.statusMessage),
                error: isResponderError(responder.status),
                success: isResponderSuccess(responder.status),
                contact: {
                    title: responder.contactName,
                    phone: responder.contactTelephone,
                    email: responder.contactEmail,
                    emergencyPhone: responder.alarmCentralTelephone,
                },
            }));
        });

        const klimMessage = computed((): IUnaMessage => {
            const klimData = props.klimData;
            if (klimData && !klimData.isResponseReceived) {
                return {
                    title: `<img src="/img/logo-klim.svg" class="klim-pictogram" alt="Voor KLIM-leden geldt een antwoordtermijn van 15 werkdagen." /> Aanwezige KLIM-leden nog niet gekend`,
                    content: 'De planaanvraag werd ook doorgestuurd naar KLIM, maar KLIP heeft het antwoord van KLIM nog niet ontvangen.',
                };
            }
            return undefined;
        });

        return {
            unaUnknown,
            unaAlert,
            unaResponders,
            klimMessage,
            helpLink,
            escapeText
        };
    }
});
